<template>
  <div v-if="!loading && pages.length && !_.get(pages, '0.items', []).length" class="p-5 mt-5">
    <div :class="['w-full flex flex-col justify-center', singleColumn ? '' : 'md:w-156 2xl:w-239 ml-1']">
      <slot name="empty">
        <NotFound text="text-no-my-ads" class="max-w-lg mx-auto" position="bottom">
          <div class="flex justify-center mb-7">
            <AddAdButton />
          </div>
        </NotFound>
      </slot>
    </div>
  </div>
  <div v-else class="p-5 mt-5">
    <div v-if="loading && !pages.length" class="w-full flex justify-center">
      <div :class="gridClass">
        <ProductFeedLoader :limit="singleColumn ? 1 : 6" />
      </div>
    </div>
    <div v-else class="w-full flex justify-center">
      <div :class="gridClass">
        <RenderProductCard
          v-for="variant in variants"
          :key="variant.id"
          :variant="variant"
          :class-name="_.get(variant, 'product.status', STATUS.DRAFT) !== STATUS.ACTIVE ? 'opacity-50' : ''"
        />
        <AddAdButton class="flex items-center justify-center rounded-xxl w-full sm:w-74 h-full text-primary-dark border border-primary-neutral hover:text-light bg-accent-dark hover:bg-primary-dark-hover overflow-hidden transition-all duration-200 transform hover:-translate-y-0.5 min-h-96" />
      </div>
    </div>
    <div v-if="hasNextPage || loadingMore" class="flex justify-center mt-8 lg:mt-12">
      <Button
        variant="outlinePanel"
        :loading="loadingMore"
        class-name="text-sm md:text-base font-semibold h-11"
        @click="handleLoadMore"
      >
        {{ $t("text-load-more") }}
      </Button>
    </div>
  </div>
</template>

<script setup>
import _ from 'lodash-es';
import { API_ENDPOINTS } from "@utils/api/endpoints";
import { STATUS } from "@utils/constants";
import { useSelectedStore } from '@stores/selected'
import { useCustomerStore } from '@stores/customer'
import ProductFeedLoader from "@components/ui/loaders/product-feed-loader";
import RenderProductCard from "@components/product/render-product-card";
import Button from "@components/ui/button";
import AddAdButton from "@components/layout/navbar/add-ad-button";
import NotFound from "@components/common/not-found";

const props = defineProps({
  status: {
    type: String,
    default: null
  },
  singleColumn: {
    type: Boolean,
    default: false
  }
});

const { t } = useI18n();
const { $eventBus, $toast } = useNuxtApp();

const store = useSelectedStore();
const customer = useCustomerStore();
const route = useRoute();

const pages = ref([]);
const pagination = ref({ page: 1, pageCount: 1, itemsPerPage: 20, itemCount: 0 });
const loading = ref(false);
const hasNextPage = computed(() => pagination.value.page < pagination.value.pageCount);
const loadingMore = ref(false);
const gridClass = computed(() => [
  'w-full sm:w-fit grid gap-x-9 gap-y-2.5 sm:gap-y-9',
  props.singleColumn ? 'grid-cols-1' : 'md:grid-cols-2 2xl:grid-cols-3'
]);

const variants = computed(() => loading.value && !pages.value.length
  ? []
  : _.flatMap(pages.value, (page) => _.flatMap(page?.items || [], (products) => products?.variants || []))
);

const updatePageSupplier = (page, supplier) => {
  _.forEach(page?.items || [], product => {
    product.supplier = supplier;
    _.forEach(product?.variants || [], variant => {
      variant.product = product;
      variant.auction = _.chain(variant?.auctions || [])
        .sortBy(['startAt', 'finishAt'])
        .first()
        .value() || null;
    });
  });
}

const updateSupplier = (supplier) => {
  _.forEach(pages.value, page => updatePageSupplier(page, supplier));
}

watch(() => customer.supplier, updateSupplier);

const reload = _.debounce(async (pending = loading) => {
  pending.value = true;

  const { data, error, status } = await useApiFetch(API_ENDPOINTS.ESHOP_PRODUCT, {
    method: 'GET',
    params: {
      page: pagination.value.page,
      limit: pagination.value.itemsPerPage,
      ...(props.status ? { status: props.status } : {})
    }
  });
  if (status.value === 'error') {
    if (import.meta.client) {
      $toast.error(t('error.product-list.load'));
    }
    sentryCaptureEvent({
      message: 'product-feed.reload',
      level: 'error',
      extra: { error: error.value }
    });
  } else {
    updatePageSupplier(data.value, customer.supplier);
    pages.value.push(data.value);
    pagination.value = data.value.pagination;
  }
  pending.value = false;
}, 500, { leading: false, trailing: true });

const handleLoadMore = async () => {
  pagination.value.page++;
  await reload(loadingMore);
}

watch(() => route.query, async () => {
  if (loading.value || loadingMore.value) {
    return;
  }
  if (import.meta.server || navigator?.onLine) {
    pages.value = [];
    pagination.value.page = 1;
    await reload();
  }
});

const variantUpdate = (value) => {
  const variant = _.find(variants.value, { id: value.id });
  if (variant) {
    _.assignIn(variant, value);
    store.setLoadResetOthers();
  }
};

onServerPrefetch(async () => {
  pagination.value.page = 1;
  // await reload();
});

onMounted(async () => {
  if (_.isEmpty(pages.value) && (import.meta.server || navigator?.onLine)) {
    pages.value = [];
    pagination.value.page = 1;
    await reload();
  }
  $eventBus.on('variant:update', variantUpdate);
});

onUnmounted(() => {
  $eventBus.off('variant:update', variantUpdate);
});

</script>
